import { DisputeRemarkNote, ReportNoteBox, Switch, hasDispute, hasSettledRemark } from '@app/components/core';
import { TabBar } from '@app/components/core/tab-bar';
import { runViewTransition } from '@app/components/core/viewTransition';
import { Language } from '@app/constants/enums';
import { cardClass, tabClass } from '@app/constants/styles';
import { formatDateString } from '@app/utils';
import { useOrganizationId } from '@app/utils/hooks';
import {
  SprintProductParamsType,
  SprintProductReportsParams,
  useListSprintProducts,
  useProduct,
  useSprint,
  useSprintProductReports,
} from '@data';
import { transformSprintProductReportsData } from '@data/api/utils/helpers';
import { useOrganizationChapters } from '@data/hooks/organization';
import ExpandableContent from '@app/components/shared/expandableContent';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Loader } from '@ui';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import ProductHeader from './ProductHeader';
import RoleSummary from './RoleSummary';
import DayLog from './DayLog';
import PricedSprintTotals from './PricedSprintTotals';
import { offersFilter, peopleIn, reportsOf } from './reportFilter';
import { daysInSprintOrder, rolesByHours, totalMinutesOf } from './sprintViews';
import { markSprintVisited } from '../visitedSprints';

const SprintProductContainer = (): ReactElement => {
  const organizationId = useOrganizationId() as string;
  const { sprintId, productId } = useParams() as Pick<SprintProductParamsType, 'sprintId' | 'productId'>;
  // Weekends are kept in the day view rather than filtered out of it. Dropping them here would drop a
  // Saturday's reports from the by-day totals while by-team still counted them, and the two views have
  // to agree. Days nobody reported on are removed further down, which is what the weekend hiding was
  // really for.
  const hideWeekend = false;
  // Opening the report is what clears its "new" mark on the product's Sprints tab.
  useEffect(() => {
    markSprintVisited(productId, sprintId);
  }, [productId, sprintId]);
  const { data: sprint } = useSprint(sprintId);
  const { data: product } = useProduct(productId);
  // Only a client reads this page, and the API answers them with the report as it was sent.
  const { data: sprintProducts, isLoading: isSprintProductsLoading } = useListSprintProducts({ sprintId, productId });
  const sprintProductReportParams: SprintProductReportsParams = { sprint, productId };
  const { data: sprintProductReports } = useSprintProductReports(sprintProductReportParams);
  // includeDisabled: render labels for historical involvements pointing at chapters the admin
  // has since disabled. The picker (ReportModal) stays on the active-only default.
  const { data: chapters } = useOrganizationChapters(organizationId, { includeDisabled: true });

  /*
   * Whose time the report is showing. The same control our own people have on their view of this sprint:
   * the hours are the client's to read, and "which of these five was on the migration" is as much their
   * question as ours. Narrowing changes nothing about the report, the sprint's total included — see
   * `reportFilter`.
   */
  const [selectedMembershipIds, setSelectedMembershipIds] = useState<string[]>([]);
  const people = useMemo(() => peopleIn(sprintProductReports), [sprintProductReports]);
  const reportsInView = useMemo(
    () => reportsOf(sprintProductReports, selectedMembershipIds),
    [sprintProductReports, selectedMembershipIds],
  );

  const { detailed, brief } = transformSprintProductReportsData(hideWeekend, sprint, reportsInView);
  /*
   * The sprint's own hours, never the filter's. "Total hours" is what the sprint came to and what an
   * approval is given against, so narrowing to one person must not appear to change it — the figure would
   * read as the sprint having shrunk. The views below are the filtered ones.
   */
  const { detailed: everyone, brief: everyoneByRole } = transformSprintProductReportsData(
    hideWeekend,
    sprint,
    sprintProductReports,
  );

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [view, setView] = useState<'role' | 'day'>('role');
  // Owned here rather than inside either view: the reader switches between them and the control has to
  // hold its position, which is what makes the two read as one report.
  const [expandAll, setExpandAll] = useState(false);

  const roles = rolesByHours(brief);
  const days = daysInSprintOrder(detailed);
  const totalMinutes = totalMinutesOf(everyone);

  const isReportLoading = !detailed || !brief || !chapters;
  // One empty state whichever view is showing — a sprint with no time on it has nothing to say twice.
  const isEmpty = !isReportLoading && days.length === 0;

  const resolveRoleLabel = (key: string): string => chapters?.items?.find(c => c.role === key)?.text ?? key;

  const sprintProduct = sprintProducts?.items?.[0];

  return (
    <div className="w-full">
      {!sprint || !product || !sprintProduct || isSprintProductsLoading ? (
        <Loader />
      ) : (
        <>
          <ProductHeader product={product} sprintProduct={sprintProduct} />

          <div className={`min-h-[560px] ${cardClass}`}>
            {/* The totals go under the summary on a phone, rather than squeezing it into a column. */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-muted-foreground">
                  {`${formatDateString(sprint.startDate, language as Language, 'dd MMM')} – ${formatDateString(
                    sprint.endDate,
                    language as Language,
                    'dd MMM yyyy',
                  )}`}
                </p>
                {/* A remark stands beside the summary it is about, so the client can see what they asked
                    for against the report it was asked about. Both are titled inside their own box, which
                    is what keeps the two level without spending a heading's height on either. */}
                <div className="mt-6 flex flex-wrap items-stretch gap-4">
                  <ReportNoteBox title={t('sprintSummary')}>
                    <ExpandableContent content={sprintProduct.summary} />
                  </ReportNoteBox>
                  {/* Answered as well as standing. Gated on `hasDispute` alone the box vanished the moment
                      we answered — taking the client's own remark with it, and leaving the report back in
                      front of them with no sign that anything had happened to what they asked. */}
                  {(hasDispute(sprintProduct) || hasSettledRemark(sprintProduct)) && (
                    <DisputeRemarkNote sprintProduct={sprintProduct} settled={hasSettledRemark(sprintProduct)} />
                  )}
                </div>
                {/* The approval comment is deliberately not here: it belongs to the approval, which the
                    "Approved" chip in the header opens in full. */}
              </div>
              <PricedSprintTotals
                readOnly
                product={product}
                sprintId={sprint.id}
                sprintStart={sprint.startDate.slice(0, 10)}
                groups={everyoneByRole ?? []}
                totalMinutes={totalMinutes}
              />
            </div>

            {/* The expand-all sits on the tab row itself, so it reads as belonging to both views rather
                than to whichever one happens to be showing. */}
            <div className="mb-6 mt-6 flex flex-col gap-3 md:flex-row md:items-end md:gap-6 md:border-b md:border-border">
              <TabBar className="md:border-b-0" activeKey={view}>
                <button
                  type="button"
                  aria-pressed={view === 'role'}
                  onClick={() => view !== 'role' && runViewTransition('fade', () => setView('role'))}
                  className={tabClass(view === 'role')}
                >
                  {t('byTeam')}
                </button>
                <button
                  type="button"
                  aria-pressed={view === 'day'}
                  onClick={() => view !== 'day' && runViewTransition('fade', () => setView('day'))}
                  className={tabClass(view === 'day')}
                >
                  {t('byDay')}
                </button>
              </TabBar>
              {/* Beside the expand, and on the tab row rather than in a bar of its own: like the expand, it
                  belongs to both views of the report. */}
              <div className="flex flex-wrap items-center gap-3 md:ml-auto md:flex-nowrap md:gap-4 md:pb-3">
                {offersFilter(people) && (
                  <MultiSelectField
                    label={<span className="sr-only">{t('titles.members')}</span>}
                    options={people}
                    value={selectedMembershipIds}
                    onChange={setSelectedMembershipIds}
                    emptyLabel={t('allMembers')}
                    searchable={people.length > 6}
                    searchPlaceholder={t('search')}
                    emptySearchLabel={t('noMatches')}
                    renderCountSummary={count => t('nItemsSelected', { count })}
                    className="w-full gap-0 md:w-52"
                  />
                )}
                <Switch value={expandAll} onChange={() => setExpandAll(value => !value)} text={t('titles.expandAll')} />
              </div>
            </div>

            {isReportLoading && <Loader />}
            {isEmpty && <p className="text-sm text-muted-foreground">{t('noReportedTimeOnSprint')}</p>}
            {!isReportLoading &&
              !isEmpty &&
              (view === 'role' ? (
                <RoleSummary groups={roles} resolveLabel={resolveRoleLabel} expandAll={expandAll} />
              ) : (
                <DayLog days={days} chapters={chapters?.items} expandAll={expandAll} />
              ))}
          </div>
        </>
      )}
    </div>
  );
};

export default SprintProductContainer;
